/**
 * Gift-with-purchase engine.
 *
 * Liquid renders the correct state on load (snippets/gift-campaigns-resolve.liquid).
 * This file exists only for the four things Liquid cannot do: auto-add,
 * auto-revoke, no-reload updates, and checkout gating. See the spec at
 * docs/superpowers/specs/2026-07-23-cart-free-gift-design.md
 *
 * No dependencies. Vanilla Web Components only.
 */
(function () {
  'use strict';

  var CONFIG_EL = 'gift-campaigns-config';

  /**
   * Shopify's `t` filter HTML-escapes every translation whose key does not
   * end in _html, so the config JSON carries "You&#39;ve", not "You've".
   * The JS assigns copy via textContent, which would show the raw entity.
   *
   * Decoded with an explicit map rather than the innerHTML-on-a-textarea
   * trick: this needs no DOM, so it is unit-testable, and the entity set is
   * closed — these five are exactly what Shopify's escaper emits. A single
   * left-to-right pass is deliberate; sequential replaces would turn an
   * escaped "&amp;lt;" into "<" by decoding twice.
   */
  var ENTITIES = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&#x27;': "'",
  };

  function decodeEntities(value) {
    if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
    return value.replace(/&(?:amp|lt|gt|quot|#39|#x27);/g, function (match) {
      return ENTITIES[match];
    });
  }

  function readConfig() {
    var el = document.getElementById(CONFIG_EL);
    if (!el) return null;
    var config;
    try {
      config = JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
    if (config && config.strings) {
      Object.keys(config.strings).forEach(function (key) {
        config.strings[key] = decodeEntities(config.strings[key]);
      });
    }
    return config;
  }

  function isGiftLine(item) {
    return !!(item.properties && item.properties._gift_campaign);
  }

  /**
   * Subscription lines never qualify for a gift: the plan already carries its
   * own quarterly gifts, so counting one toward a cart threshold pays twice.
   *
   * `gift-campaigns-resolve.liquid` applies the identical exclusion. Both are
   * required — that file decides whether the progress strip renders, this one
   * decides whether a gift line is actually added, and a cart where those two
   * disagree gets a gift it was never shown earning.
   */
  function isSubscriptionLine(item) {
    return !!item.selling_plan_allocation;
  }

  /** A line that can qualify for a gift: neither a gift nor a subscription. */
  function isQualifyingLine(item) {
    return !isGiftLine(item) && !isSubscriptionLine(item);
  }

  /**
   * Locale- and market-aware endpoints. window.routes is defined in
   * layout/theme.liquid and carries the active market prefix; hardcoding
   * /cart/... breaks under localized URLs exactly as the Liquid forms did
   * before theme-check flagged them. Falls back to the root paths so the
   * engine stays testable outside a storefront.
   */
  function cartRoutes() {
    var r = (typeof window !== 'undefined' && window.routes) || {};
    return {
      cart: r.cart_url || '/cart',
      add: r.cart_add_url || '/cart/add',
      change: r.cart_change_url || '/cart/change',
    };
  }

  class GiftEngine {
    constructor(config) {
      this.config = config;
      this.campaigns = (config && config.campaigns) || [];
      // Defensive: never trust the emitted order (spec 6.3).
      this.campaigns.forEach(function (c) {
        c.tiers.sort(function (a, b) {
          return b.threshold - a.threshold;
        });
      });
      this.deadCampaigns = {};
      this.decidedFingerprint = null;
    }

    /**
     * Retries 429 with linear backoff. A network-level rejection resolves to
     * null rather than throwing: every caller treats null as "leave the cart
     * alone", which is the spec 6.8 behaviour, and it keeps the checkout gate
     * failing open instead of rejecting up the stack.
     */
    async fetchWithRetry(url, options, maxRetries) {
      var retries = typeof maxRetries === 'number' ? maxRetries : 3;
      for (var attempt = 0; attempt <= retries; attempt++) {
        var res;
        try {
          res = await fetch(url, options);
        } catch (e) {
          return null;
        }
        if (res.status === 429 && attempt < retries) {
          await new Promise(function (r) {
            setTimeout(r, 1000 * (attempt + 1));
          });
          continue;
        }
        return res;
      }
      return null;
    }

    async readCart() {
      var res = await this.fetchWithRetry(cartRoutes().cart + '.js', { credentials: 'same-origin' });
      if (!res || !res.ok) return null;
      try {
        return await res.json();
      } catch (e) {
        return null;
      }
    }

    /**
     * Fingerprint of the customer's own lines only. Gift lines are excluded so
     * the engine never reacts to its own mutations (spec 6.1).
     */
    nonGiftFingerprint(cart) {
      return (cart.items || [])
        .filter(function (i) {
          return !isGiftLine(i);
        })
        .map(function (i) {
          return i.variant_id + ':' + i.quantity;
        })
        .join(',');
    }

    /**
     * Qualifying total for a campaign. Uses final_line_price (discounted) and
     * counts only qualifyingHandles; an empty list means the whole cart (spec 6.2).
     *
     * item.handle is the product handle on the AJAX cart JSON. Note that the
     * Liquid half of this rule must use item.product.handle — a Liquid
     * line_item has no handle property at all.
     */
    measure(cart, campaign) {
      var scoped = campaign.qualifyingHandles.length > 0;
      var byQuantity = campaign.conditionType === 'qualifying_quantity';
      var total = 0;
      (cart.items || []).forEach(function (item) {
        if (!isQualifyingLine(item)) return;
        if (scoped && campaign.qualifyingHandles.indexOf(item.handle) === -1) return;
        if (byQuantity) {
          total += item.quantity;
        } else {
          var line =
            typeof item.final_line_price === 'number'
              ? item.final_line_price
              : item.price * item.quantity;
          total += line / 100;
        }
      });
      return total;
    }

    /** Highest threshold the cart meets, or null. */
    resolveTier(campaign, measured) {
      for (var i = 0; i < campaign.tiers.length; i++) {
        if (measured >= campaign.tiers[i].threshold) return campaign.tiers[i];
      }
      return null;
    }

    /** Lowest threshold not yet met, or null. */
    nextTier(campaign, measured) {
      var next = null;
      campaign.tiers.forEach(function (t) {
        if (measured < t.threshold && (!next || t.threshold < next.threshold)) next = t;
      });
      return next;
    }

    countTaken(cart, campaignName) {
      return (cart.items || []).reduce(function (sum, item) {
        if (item.properties && item.properties._gift_campaign === campaignName) {
          return sum + item.quantity;
        }
        return sum;
      }, 0);
    }

    async addGift(campaignName, variantId, quantity, auto) {
      var res = await this.fetchWithRetry(cartRoutes().add + '.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: quantity,
              properties: {
                _gift_campaign: campaignName,
                _auto_added: auto ? 'true' : 'false',
              },
            },
          ],
        }),
      });
      if (!res || !res.ok) {
        // Permanent 4xx (422 out of stock, invalid variant) — stop trying this
        // campaign for the session so we don't retry on every cart change.
        if (res && res.status >= 400 && res.status < 500 && res.status !== 429) {
          this.deadCampaigns[campaignName] = true;
        }
        return false;
      }
      return true;
    }

    /**
     * Change a line's quantity.
     *
     * identifier is either a 1-based line index (number) or a line item key
     * (string). Prefer the key whenever more than one mutation will be issued
     * before the cart is re-read: removing a line re-indexes every line after
     * it, so indices captured from an earlier snapshot go stale mid-pass.
     * Shopify recommends the key for the same reason, since several lines can
     * share a variant_id when their properties differ — which is exactly the
     * case here, where a gift line and a bought line can be the same variant.
     */
    async changeLine(identifier, quantity) {
      var body =
        typeof identifier === 'number'
          ? { line: identifier, quantity: quantity }
          : { id: String(identifier), quantity: quantity };
      var res = await this.fetchWithRetry(cartRoutes().change + '.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      return !!(res && res.ok);
    }

    /** Variant ids offered by a tier, availability filtered. */
    tierVariantIds(tier) {
      var ids = [];
      tier.options.forEach(function (opt) {
        opt.variants.forEach(function (v) {
          if (v.available) ids.push(v.id);
        });
      });
      return ids;
    }

    /**
     * The most units a tier can legitimately hand out. Normally choose_count,
     * but a tier offering a single option with duplicates disallowed can only
     * ever yield one unit, however large choose_count is (spec 5.4, 7.2).
     */
    tierCapacity(tier) {
      if (tier.allowDuplicates) return tier.chooseCount;
      var distinct = this.tierVariantIds(tier).length;
      return Math.min(tier.chooseCount, distinct);
    }

    /**
     * Bring the cart in line with the winning tier (spec 6.4). One rule covers
     * every transition, because validity is judged against the winning tier's
     * option set rather than against tier identity:
     *   - revoke gift units not offered by the winning tier
     *   - trim newest-first when over choose_count
     *   - auto-add when there is exactly one option with exactly one variant
     */
    async reconcile(cart) {
      var revoked = [];
      var changed = false;

      // Empty-cart guard (spec 5.5, 6.3). A threshold-0 tier means "any order",
      // and an empty cart measures 0 — without this, such a campaign would keep
      // re-adding a gift to an empty cart. Counting NON-GIFT lines also handles
      // a cart stripped down to nothing but gifts: those must be revoked.
      // Subscriptions are excluded here too, so a cart holding nothing but
      // subscriptions has no qualifying lines and every gift is revoked —
      // matching the progress strip, which does not render for that cart.
      var hasRealLines = (cart.items || []).some(isQualifyingLine);

      for (var ci = 0; ci < this.campaigns.length; ci++) {
        var campaign = this.campaigns[ci];
        if (this.deadCampaigns[campaign.name]) continue;

        var measured = this.measure(cart, campaign);
        var tier = hasRealLines ? this.resolveTier(campaign, measured) : null;
        var allowed = tier ? this.tierVariantIds(tier) : [];

        // Collect this campaign's gift lines. `line` is kept only to order them
        // newest-first; every mutation goes through the stable `key`.
        var lines = [];
        (cart.items || []).forEach(function (item, index) {
          if (item.properties && item.properties._gift_campaign === campaign.name) {
            lines.push({ line: index + 1, key: item.key, item: item });
          }
        });

        // Invalid units first: wrong tier's gifts, or no tier at all.
        var keep = [];
        for (var li = lines.length - 1; li >= 0; li--) {
          var entry = lines[li];
          if (allowed.indexOf(entry.item.variant_id) === -1) {
            if (await this.changeLine(entry.key, 0)) {
              revoked.push(entry.item.product_title || entry.item.title);
              changed = true;
            }
          } else {
            keep.push(entry);
          }
        }
        if (!tier) continue;

        // Over-allocation: trim newest-first (highest line index).
        keep.sort(function (a, b) {
          return b.line - a.line;
        });
        var held = keep.reduce(function (s, e) {
          return s + e.item.quantity;
        }, 0);
        for (var ki = 0; ki < keep.length && held > tier.chooseCount; ki++) {
          var over = held - tier.chooseCount;
          var take = Math.min(over, keep[ki].item.quantity);
          if (await this.changeLine(keep[ki].key, keep[ki].item.quantity - take)) {
            revoked.push(keep[ki].item.product_title || keep[ki].item.title);
            held -= take;
            changed = true;
          } else {
            break;
          }
        }

        // Under-allocation: auto-add only when nothing is left to decide.
        var capacity = this.tierCapacity(tier);
        if (held < capacity) {
          var available = tier.options.length === 1
            ? tier.options[0].variants.filter(function (v) {
                return v.available;
              })
            : [];
          if (available.length === 1) {
            if (await this.addGift(campaign.name, available[0].id, capacity - held, true)) {
              changed = true;
            }
          }
        }
      }

      return { changed: changed, revoked: revoked, notice: null };
    }
  }

  var engine = null;

  function getEngine() {
    if (!engine) {
      var config = readConfig();
      if (config) engine = new GiftEngine(config);
    }
    return engine;
  }

  var GIFT_SECTIONS = ['cart-gift-progress', 'cart-gift-picker'];

  function sectionInnerHTML(html, selector) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var el = parsed.querySelector(selector);
    return el ? el.innerHTML : null;
  }

  /**
   * Dawn's own cart sections, which our mutations invalidate.
   *
   * A gift is a real cart line, so auto-adding or revoking one changes the
   * line list and the item count. Refreshing only the two gift sections would
   * leave the picker reading "1 of 2 chosen" beside a cart that does not show
   * the gift. Mirrors getSectionsToRender() in assets/cart.js.
   */
  function dawnSectionsToRender() {
    var out = [];
    var items = document.getElementById('main-cart-items');
    if (items) {
      out.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
    }
    var footer = document.getElementById('main-cart-footer');
    if (footer) {
      out.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
    }
    if (document.getElementById('cart-icon-bubble')) {
      out.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    }
    if (document.getElementById('cart-live-region-text')) {
      out.push({
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      });
    }
    return out;
  }

  /**
   * True when the cart drawer is the surface the customer is looking at.
   *
   * Shared by refreshSections() and renderRevokeNotice() so both agree on
   * which surface is active — computing this two different ways would risk
   * one path refreshing the drawer while the other writes a notice into the
   * hidden page markup, or vice versa.
   */
  function drawerIsActive() {
    var drawerEl = document.querySelector('cart-drawer');
    return !!(drawerEl && drawerEl.classList.contains('active'));
  }

  /**
   * The drawer renders every gift part inside the single `cart-drawer`
   * section, so one request replaces progress, picker, items and footer
   * together — no chunking, unlike the page's six-section batch.
   */
  function drawerSectionsToRender() {
    var out = [];
    if (document.querySelector('cart-drawer')) {
      out.push({ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' });
    }
    if (document.getElementById('cart-icon-bubble')) {
      out.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    }
    return out;
  }

  /** Dawn keeps the empty state on wrappers that sit outside .js-contents. */
  function toggleEmptyState(cart) {
    if (!cart) return;
    var isEmpty = cart.item_count === 0;
    var cartItems = document.querySelector('cart-items');
    if (cartItems) cartItems.classList.toggle('is-empty', isEmpty);
    var footer = document.getElementById('main-cart-footer');
    if (footer) footer.classList.toggle('is-empty', isEmpty);
  }

  /**
   * Re-fetch the affected sections and swap their innards in place.
   *
   * Dawn's cart.js piggybacks `sections:` onto the mutation request body and
   * reads response.sections. We deliberately do not: one reconcile() pass may
   * issue several mutations, so the sections attached to any single response
   * can be stale by the time the pass finishes. One standalone render after
   * reconciliation has settled is correct and costs one request per
   * interaction rather than per mutation.
   */
  /**
   * Our two sections, paired with the id the Section Rendering API expects.
   *
   * A section declared in a JSON template is addressed as
   * template--<template_id>__<key>, not as the bare key — requesting the bare
   * key returns 400 for the whole batch. The id is therefore read from
   * data-section-id, which Liquid fills from {{ section.id }}.
   *
   * The selector is scoped to [data-gift-surface="page"] so it can never
   * match a drawer host. theme.liquid renders the drawer's <gift-progress>/
   * <gift-picker> before <main>, so on cart_type: drawer the drawer host
   * precedes the page host in document order. An unscoped selector would let
   * querySelector's first-match semantics silently pick the drawer host
   * (which omits data-section-id) instead of the page's, leaving the page's
   * gift progress/picker permanently stale — the drawer host shadows the
   * page host rather than being skipped. Scoping by surface removes the
   * shadowing outright rather than relying on the data-section-id guard
   * below to catch it after the fact.
   */
  function giftSectionsToRender() {
    var out = [];
    GIFT_SECTIONS.forEach(function (name) {
      var selector = '[data-gift-section="' + name + '"][data-gift-surface="page"]';
      var host = document.querySelector(selector);
      if (host && host.dataset.sectionId) {
        out.push({ selector: selector, host: host, section: host.dataset.sectionId });
      }
    });
    return out;
  }

  /**
   * The Section Rendering API rejects a request asking for more than five
   * sections — the whole batch 400s, it does not partially succeed. We need
   * six (two gift sections, the cart items, the footer, the count bubble and
   * the live region), so the ids are chunked rather than truncated: dropping
   * one silently would leave part of the page stale with no signal.
   */
  var SECTIONS_PER_REQUEST = 5;

  async function fetchSections(ids) {
    var base = cartRoutes().cart;
    var chunks = [];
    for (var i = 0; i < ids.length; i += SECTIONS_PER_REQUEST) {
      chunks.push(ids.slice(i, i + SECTIONS_PER_REQUEST));
    }
    var parts = await Promise.all(
      chunks.map(function (chunk) {
        return fetch(base + '?sections=' + encodeURIComponent(chunk.join(',')), {
          credentials: 'same-origin',
        })
          .then(function (r) {
            return r.ok ? r.json() : {};
          })
          .catch(function () {
            return {};
          });
      })
    );
    var merged = {};
    parts.forEach(function (part) {
      Object.keys(part).forEach(function (k) {
        merged[k] = part[k];
      });
    });
    return merged;
  }

  async function refreshSections(cartChanged) {
    var drawerActive = drawerIsActive();
    var gifts = giftSectionsToRender();

    // The page's own sections are ALWAYS refreshed. On /cart with the drawer
    // open both surfaces are live: the drawer is what the customer is looking
    // at, and the page behind it must not be left showing pre-mutation items,
    // totals and live-region text for whenever they close the drawer. Picking
    // one surface (the drawer when it happened to be open) left the page
    // stale until a full reload. dawnSectionsToRender() is null-guarded on
    // every lookup, so off /cart it contributes at most the count bubble.
    //
    // The drawer's own section is added only while it is OPEN. Adding it
    // unconditionally would inflate every cart-page mutation with a
    // drawer-section render, which is precisely what the lazy data-stale
    // flag below exists to avoid.
    var dawn = dawnSectionsToRender();
    if (drawerActive) {
      drawerSectionsToRender().forEach(function (s) {
        var seen = dawn.some(function (existing) {
          return existing.id === s.id;
        });
        // cart-icon-bubble is in both lists; requesting it twice would burn a
        // slot against the five-per-request cap for nothing.
        if (!seen) dawn.push(s);
      });
    }

    var ids = gifts.concat(dawn).map(function (s) {
      return s.section;
    });
    if (!ids.length) return;

    // A reconciliation pass can add or revoke gift lines without going
    // through Dawn's cart-update pubsub event at all, so cart.js's own
    // stale-marking (in CartItems#onCartUpdate) never fires for this path.
    // Mark the hidden drawer stale here too so it doesn't show gift lines
    // out of sync with what the page just settled on.
    //
    // Only when the pass actually changed the cart. A no-op reconcile — every
    // /cart load runs one from the DOMContentLoaded bootstrap — leaves the
    // drawer's server-rendered markup exactly correct, and stamping it stale
    // anyway made the customer's first drawer open always pay a full round
    // trip before animating.
    if (cartChanged && !drawerActive) {
      var drawerEl = document.querySelector('cart-drawer');
      if (drawerEl) drawerEl.setAttribute('data-stale', '');
    }

    var data = await fetchSections(ids);

    // Gift sections: swap only the custom element's contents so the element
    // instance — and the delegated submit listener bound to it — survives.
    gifts.forEach(function (s) {
      if (!data[s.section]) return;
      var fresh = sectionInnerHTML(data[s.section], s.selector);
      if (fresh !== null) s.host.innerHTML = fresh;
    });

    dawn.forEach(function (s) {
      if (!data[s.section]) return;
      var root = document.getElementById(s.id);
      if (!root) return;
      var target = root.querySelector(s.selector) || root;
      var fresh = sectionInnerHTML(data[s.section], s.selector);
      if (fresh !== null) target.innerHTML = fresh;
    });
  }

  /**
   * Where the notice goes depends on which surface the customer is looking
   * at, unlike giftSectionsToRender() which always targets the page. The
   * drawer host precedes the page host in document order (theme.liquid
   * renders the drawer before <main>), so an unscoped querySelector would
   * always resolve to the drawer host — on a cart page with the drawer
   * closed, the notice would land in hidden markup and the customer would
   * never see it. drawerIsActive() is the single source of truth for which
   * surface is live, shared with refreshSections() so the two never
   * disagree about where the customer's attention is.
   *
   * Surface resolution is three steps, not the obvious two, because
   * drawerIsActive() can read false while the drawer is still what the
   * customer is about to see. On the quick-add-modal path, product-form.js
   * defers cart.renderContents() — and therefore CartDrawer.open()'s
   * `active`-class flip — until the modal's `modalClosed` event, an
   * arbitrary user-paced delay, while publish(cartUpdate) can complete a
   * full reconcile immediately. On a product page there is no page host to
   * fall back to, so a plain "drawer-active ? drawer : page" would drop the
   * notice on the floor for the entire window before the modal closes.
   * A plain two-step "page if present, else drawer" is not the fix either:
   * on /cart with the drawer OPEN, a page host exists but sits behind the
   * open drawer where the customer isn't looking, so that ordering would
   * inject the notice into markup nobody sees. The three steps below —
   * drawer while active, else page if one exists, else drawer as the last
   * resort — are what's needed to cover all three real states (/cart with
   * drawer open, /cart with drawer closed, product page in any state).
   * Do not simplify this back to two steps.
   */
  function renderRevokeNotice(revoked, remaining) {
    var eng = getEngine();

    var drawerHost =
      document.querySelector('[data-gift-section="cart-gift-picker"][data-gift-surface="drawer"]') ||
      document.querySelector('[data-gift-section="cart-gift-progress"][data-gift-surface="drawer"]');
    var pageHost =
      document.querySelector('[data-gift-section="cart-gift-picker"][data-gift-surface="page"]') ||
      document.querySelector('[data-gift-section="cart-gift-progress"][data-gift-surface="page"]');

    var host;
    if (drawerIsActive()) {
      host = drawerHost;
    } else if (pageHost) {
      host = pageHost;
    } else {
      host = drawerHost;
    }

    if (!host || !eng || !revoked.length) return;

    var strings = eng.config.strings;
    var title;
    if (remaining === 0) {
      // Nothing survived, so the blunt copy is accurate.
      title = strings.revokedAll;
    } else if (revoked.length === 1) {
      title = strings.revokedTitleOne;
    } else {
      title = strings.revokedTitle.replace('__COUNT__', String(revoked.length));
    }

    var notice = document.createElement('div');
    notice.className = 'gift-notice';
    notice.setAttribute('role', 'status');

    var titleEl = document.createElement('span');
    titleEl.className = 'gift-notice__title';
    titleEl.textContent = title;
    notice.appendChild(titleEl);

    var body = document.createElement('span');
    body.textContent = revoked.join(', ');
    notice.appendChild(body);

    host.prepend(notice);
  }

  var reconciling = false;

  /**
   * cartAlreadyChanged: true when the caller mutated the cart itself before
   * calling in — the picker's add/remove forms do, via addGift()/changeLine(),
   * which are plain fetches and never publish cartUpdate. The reconcile that
   * follows such a mutation usually finds nothing left to do and reports
   * changed:false, so without this hint refreshSections() would conclude
   * nothing happened and leave a hidden drawer showing the pre-add cart.
   */
  async function runReconcileAndRefresh(cartAlreadyChanged) {
    if (reconciling) return;
    reconciling = true;
    try {
      var eng = getEngine();
      if (!eng) return;
      var cart = await eng.readCart();
      if (!cart) return;
      var fingerprint = eng.nonGiftFingerprint(cart);
      var result = await eng.reconcile(cart);
      eng.decidedFingerprint = fingerprint;

      var settled = result.changed ? await eng.readCart() : cart;
      await refreshSections(result.changed || cartAlreadyChanged === true);
      toggleEmptyState(settled);

      if (result.revoked.length) {
        var remaining = eng.campaigns.reduce(function (sum, c) {
          return sum + eng.countTaken(settled || cart, c.name);
        }, 0);
        renderRevokeNotice(result.revoked, remaining);
      }
    } finally {
      reconciling = false;
    }
  }

  /**
   * Dawn's button loading treatment, lifted from assets/product-form.js so the
   * gift picker's add button behaves like every other add-to-cart on the site:
   * `.loading` turns the label transparent, the sibling spinner is un-hidden,
   * and aria-disabled (NOT disabled) blocks a second submit while leaving the
   * button focusable — a real `disabled` would move focus to the body mid-add.
   */
  function setButtonLoading(button, loading) {
    if (!button) return;
    var spinner = button.querySelector('.loading__spinner');
    if (loading) {
      button.classList.add('loading');
      button.setAttribute('aria-disabled', 'true');
      if (spinner) spinner.classList.remove('hidden');
    } else {
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');
      if (spinner) spinner.classList.add('hidden');
    }
  }

  class GiftSectionBase extends HTMLElement {
    connectedCallback() {
      this.addEventListener('submit', this.onSubmit.bind(this));
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
      }
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    /** Intercept the no-JS forms and perform the same operation over AJAX. */
    async onSubmit(event) {
      var form = event.target.closest('form');
      if (!form) return;
      var isAdd = form.classList.contains('gift-picker__form');
      var isChip = form.classList.contains('gift-picker__chip');
      if (!isAdd && !isChip) return;

      event.preventDefault();

      // The pressed control. event.submitter is read here, before any await:
      // the refresh below replaces the picker markup, and a submitter read
      // afterwards could be pointing at a detached node.
      var submitBtn = event.submitter || form.querySelector('[type="submit"]');

      // Already mid-add. Without this a double-tap on a slow connection fires
      // addGift twice, and the second call races the reconcile that would have
      // trimmed the over-allocation.
      if (submitBtn && submitBtn.getAttribute('aria-disabled') === 'true') return;

      var eng = getEngine();
      if (!eng) {
        // No engine to add over AJAX, so the native post runs and the page
        // navigates. Spin anyway — the wait is real either way.
        setButtonLoading(submitBtn, true);
        form.submit();
        return;
      }

      // Set to true only on the paths that hand off to a native form post. The
      // browser is navigating at that point, so the spinner should stay up
      // until the new document paints rather than blinking off in `finally`.
      var navigating = false;

      this.setAttribute('aria-busy', 'true');
      setButtonLoading(submitBtn, true);
      try {
        if (isAdd) {
          var campaign = form.querySelector('[name="properties[_gift_campaign]"]').value;
          var variantId = parseInt(form.querySelector('[name="id"]').value, 10);
          await eng.addGift(campaign, variantId, 1, false);
        } else {
          // Prefer the stable line key; fall back to the index the no-JS
          // form carries if an older render is still on the page.
          var key = form.getAttribute('data-line-key');
          var qty = parseInt(form.querySelector('[name="quantity"]').value, 10);
          if (key) {
            await eng.changeLine(key, qty);
          } else {
            await eng.changeLine(parseInt(form.querySelector('[name="line"]').value, 10), qty);
          }
        }
        // We just mutated the cart directly, so say so — the reconcile that
        // follows will usually report changed:false.
        await runReconcileAndRefresh(true);
      } catch (e) {
        // Fall back to the native form post rather than stranding the customer.
        navigating = true;
        form.submit();
        return;
      } finally {
        this.removeAttribute('aria-busy');
        if (!navigating) setButtonLoading(submitBtn, false);
      }
    }

    async onCartUpdate() {
      await runReconcileAndRefresh();
    }
  }

  class GiftProgress extends GiftSectionBase {}
  class GiftPicker extends GiftSectionBase {}

  if (!customElements.get('gift-progress')) customElements.define('gift-progress', GiftProgress);
  if (!customElements.get('gift-picker')) customElements.define('gift-picker', GiftPicker);

  /**
   * KNOWN GAP (spec 11): sections/main-cart-footer.liquid:133-135 renders
   * content_for_additional_checkout_buttons, so Shop Pay / PayPal / Google Pay
   * appear on the cart and bypass CHECKOUT_SELECTOR entirely. They are not
   * <button name="checkout"> inside form#cart, so neither the load-time gate
   * nor the submit interception below can see them.
   *
   * Exposure is bounded: reconcile still runs on every cart change the engine
   * observes, so the leak needs a cart mutated outside the engine's view AND
   * an express checkout. Only a Cart and Checkout Validation Function can
   * close this server-side. Deferred by decision 13, conditional on the
   * monitoring in spec 9.1 showing the leakage is material.
   */
  var CHECKOUT_SELECTOR = '.cart__checkout-button';
  var GATE_TIMEOUT_MS = 4000;
  var GATE_CLASS = 'gift-gate-pending';

  /**
   * The checkout button belonging to ONE surface.
   *
   * With settings.cart_type == 'drawer' there are two live checkout buttons
   * carrying CHECKOUT_SELECTOR — snippets/cart-drawer.liquid's
   * #CartDrawer-Checkout (form="CartDrawer-Form") and
   * sections/main-cart-footer.liquid's #checkout (form="cart") — and
   * layout/theme.liquid renders the drawer BEFORE <main>, so a bare
   * document.querySelector always resolves to the drawer's. Releasing a hold
   * placed by a click on the cart page and then re-firing that first match
   * would submit the wrong form entirely, and if the drawer's button happened
   * to be server-rendered disabled (cart == empty) the re-fire would be
   * swallowed with no signal at all. Every singular lookup therefore goes
   * through here with the surface the customer actually used.
   *
   * setCheckoutDisabled() below deliberately does NOT scope: the hold is a
   * cart-wide state, so both surfaces are gated and both are released.
   */
  function checkoutButtonFor(inDrawer) {
    var buttons = document.querySelectorAll(CHECKOUT_SELECTOR);
    for (var i = 0; i < buttons.length; i++) {
      if (!!buttons[i].closest('cart-drawer') === inDrawer) return buttons[i];
    }
    return null;
  }

  function setCheckoutDisabled(disabled) {
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach(function (btn) {
      if (disabled) {
        // Never gate a button something else already disabled. Dawn disables
        // it on an empty cart, so blanket-enabling on release would offer
        // checkout for a cart with nothing in it.
        //
        // Do not "fix" the skip by forcing the disable: that would let this
        // gate release a button Dawn disabled for an empty cart.
        if (btn.disabled) return;
        btn.disabled = true;
        btn.classList.add(GATE_CLASS);
      } else if (btn.classList.contains(GATE_CLASS)) {
        // Release only what we gated. After a section refresh the button is a
        // fresh element carrying the server's own disabled state, and it will
        // not carry our marker — so we leave that one alone.
        btn.disabled = false;
        btn.classList.remove(GATE_CLASS);
      }
    });
  }

  /**
   * Hold checkout until the first reconciliation completes, then release.
   *
   * MUST fail open. A customer must never be locked out of checkout because
   * this engine errored or hung — losing an order costs far more than an
   * occasional unearned $0 gift. A timer guarantees release regardless of
   * whether the promise ever settles.
   */
  function gateCheckoutUntilSettled(promise) {
    setCheckoutDisabled(true);
    var released = false;
    var release = function () {
      if (released) return;
      released = true;
      setCheckoutDisabled(false);
    };
    setTimeout(release, GATE_TIMEOUT_MS);
    promise.then(release, release);
    return promise;
  }

  var checkoutCleared = false;

  /**
   * Force one last reconciliation before leaving for checkout (spec 6.9).
   *
   * The load-time gate only covers changes the engine observed. A cart mutated
   * in another tab is invisible to it, so without this a stale gift would
   * travel to checkout unchallenged. Fails open on the same timer: if
   * reconciliation hangs, the customer still leaves.
   */
  async function onCartFormSubmit(event) {
    var submitter = event.submitter;
    if (!submitter || submitter.name !== 'checkout') return;
    if (checkoutCleared) return;
    if (!document.getElementById(CONFIG_EL)) return;

    // Which surface submitted, recorded BEFORE the awaits below. The refresh
    // can detach the submitter from the document, and closest() on a detached
    // node can no longer reach <cart-drawer>, so this cannot be deferred.
    var inDrawer = !!submitter.closest('cart-drawer');

    event.preventDefault();
    try {
      await Promise.race([
        runReconcileAndRefresh(),
        new Promise(function (r) {
          setTimeout(r, GATE_TIMEOUT_MS);
        }),
      ]);
    } catch (e) {
      // Swallow: proceeding with an unreconciled cart beats trapping them.
    }
    checkoutCleared = true;
    setCheckoutDisabled(false);

    // Re-fire on the surface that submitted. The original submitter is used
    // whenever it survived the refresh; only when it was detached do we go
    // looking, and then only within the same surface. A button left disabled
    // by the server (empty cart) is respected rather than forced.
    var btn = submitter.isConnected ? submitter : checkoutButtonFor(inDrawer);
    if (btn && !btn.disabled) {
      btn.click();
    }
  }

  document.addEventListener('submit', onCartFormSubmit);

  document.addEventListener('DOMContentLoaded', function () {
    if (!document.getElementById(CONFIG_EL)) return;
    gateCheckoutUntilSettled(runReconcileAndRefresh());
  });

  window.GiftEngine = GiftEngine;
  window.giftReadConfig = readConfig;
  window.giftDecodeEntities = decodeEntities;
})();
